// Per-site SMS / email CAPABILITY — the single frontend implementation.
// Import this rather than adding another copy.
//
// THE MODEL: there is no SMS_ENABLED / EMAIL_ENABLED boolean anywhere. Capability is
// DERIVED from provisioning state — a site can text if it has been given a texting
// number, and can email if it has a verified sender:
//
//   SMS   → GENERAL_PHONE            non-blank (the provisioned Twilio number)
//   EMAIL → GENERAL_VERIFIED_EMAILS  parses to a usable address
//
// DEFAULT-DENY, and deliberately no new settings key: a Core-plan office needs no
// provisioning to be correct, and an office that later buys All-in-One lights up the
// moment A2P writes GENERAL_PHONE — nothing to remember to flip.
//
// BACKEND COUNTERPART — keep these in sync:
//   teamio-serverless-api/tx-presenter-service/lib/resolveChannels.js
// That file is the canonical definition (it also returns machine-readable reasons and
// can see teamio.sites for Twilio creds, which the frontend cannot). This module
// mirrors its semantics exactly so the two never disagree about the same site.
//
// WHY THIS FILE EXISTS (2026-08-01): the check was reimplemented INLINE in ~8 places —
// config/ability.js, Layout.vue (x3), Sidebar.vue, CreditBalancePill.vue,
// AdminPage2.vue, CreateFollowup.vue (x4), CreateCampaignModal.vue — and they did not
// agree with each other:
//
//   - SMS: some tested `!!PropertyValue` (truthy-only), others `.trim() !== ''`. These
//     disagree on a whitespace-only value: `!!' '` is true, but it trims to empty. That
//     set is EMPTY in prod today (all 18 disabled sites hold a true ''), so it was a
//     latent split, not a live bug — the first ' ' saved would have made surfaces
//     silently disagree about the same site.
//   - EMAIL: every inline copy tested `PropertyValue === ''` or truthiness, which is
//     WRONG — GENERAL_VERIFIED_EMAILS has five possible shapes (see verifiedSender.js),
//     so `"[]"` or `"{}"` is non-blank yet yields NO usable sender. Those call sites
//     reported "email enabled" for a site that cannot actually send. CreateFollowup.vue
//     even used the real parser to BUILD the From address while using `=== ''` to decide
//     availability — the two could disagree in the same component.

import { pickVerifiedFromEmail } from './verifiedSender.js';

export const SMS_CAPABILITY_SETTING = 'GENERAL_PHONE';
export const EMAIL_CAPABILITY_SETTING = 'GENERAL_VERIFIED_EMAILS';

/**
 * Read one settings PropertyValue, tolerating BOTH shapes the app holds settings in.
 *
 * Components are inconsistent about which they have: the mixin exposes `allSettings`
 * (an object keyed by Property) while several components only have the raw `settings`
 * array, and some hold both and fall back between them. Accepting either keeps call
 * sites free of that plumbing — which is most of what the ~8 inline copies were doing.
 *
 * @param {object|Array|null|undefined} settings  allSettings object OR settings array
 * @param {string} property
 * @returns {string|null} raw PropertyValue, or null when absent
 */
export function readSettingValue(settings, property) {
  if (!settings || !property) return null;

  // Shape 1: allSettings — { GENERAL_PHONE: { PropertyValue: '...' } }
  if (!Array.isArray(settings)) {
    const row = settings[property];
    if (row && typeof row === 'object' && row.PropertyValue !== undefined) {
      return row.PropertyValue === null ? null : row.PropertyValue;
    }
    // Tolerate a flattened { GENERAL_PHONE: '...' } map too — cheap, and avoids a
    // silent null if some future caller passes the simpler shape.
    if (typeof row === 'string') return row;
    return null;
  }

  // Shape 2: raw settings array — [{ Property, PropertyValue }]
  const hit = settings.find((s) => s && s.Property === property);
  if (!hit || hit.PropertyValue === undefined || hit.PropertyValue === null) return null;
  return hit.PropertyValue;
}

/**
 * Has this site been GIVEN a texting number? TRIM-AWARE by design — a whitespace-only
 * number is not a number. Mirrors resolveChannels.js, which trims before testing.
 *
 * This is the PROVISIONING question, not "may we send an SMS" — see isSmsEnabled. A
 * WhatsApp-only office (SMS_OUTBOUND_DISABLED) still has a number: inbound SMS still
 * arrives, its credit wallet still exists, and Conversations must still show. Use this for
 * those; use isSmsEnabled for anything that SENDS.
 *
 * @param {object|Array|null|undefined} settings
 * @returns {boolean}
 */
export function hasSmsNumber(settings) {
  const raw = readSettingValue(settings, SMS_CAPABILITY_SETTING);
  return typeof raw === 'string' && raw.trim() !== '';
}

// ── WhatsApp-only offices (2026-10-07) ──────────────────────────────────────
// SMS_OUTBOUND_DISABLED is a per-site STANDING POLICY set by Teamio (Super Admin ▸
// Messaging safety): the practice keeps its number and inbound SMS, but nothing may be
// SENT by SMS — patients are messaged on WhatsApp. The engine enforces it server-side
// (teamio-lambda-layer killSwitch.isSiteSmsOutboundDisabled); this mirrors its parse so
// the UI never offers a send the server will refuse.
// Agreement with the layer is EXECUTED by cypress/node-tests/sms-outbound-policy-agreement.test.js.
export const SMS_OUTBOUND_POLICY_SETTING = 'SMS_OUTBOUND_DISABLED';
export const WHATSAPP_ENABLED_SETTING = 'WHATSAPP_ENABLED';

/** Staff-facing reason wherever a text action is unavailable because of the policy. */
export const SMS_DISABLED_MESSAGE = 'Texting is turned off for this office. Message patients on WhatsApp instead.';

/**
 * Why WhatsApp can't be switched off at a WhatsApp-only office. WORD FOR WORD the server's 409
 * (conversations-service lib/whatsappSettings.js WHATSAPP_OFF_BLOCKED_MESSAGE) — pinned by
 * cypress/node-tests/whatsapp-only-admin-controls.test.js, so the locked switch and a refused save
 * say the same thing.
 */
export const WHATSAPP_REQUIRED_MESSAGE = "Texting is turned off for this office, so WhatsApp is the only way to message patients and can't be turned off. Contact Teamio to turn texting back on first.";

/**
 * Is outbound SMS switched off for this site? The layer's parse, exactly: ONLY
 * true / 1 / yes (trimmed, any case). Blank, '0', 'false' and garbage read as NOT
 * disabled — a malformed row must never silently stop a practice's texting.
 *
 * @param {object|Array|null|undefined} settings
 * @returns {boolean}
 */
export function isSmsOutboundDisabled(settings) {
  const raw = readSettingValue(settings, SMS_OUTBOUND_POLICY_SETTING);
  const v = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  return v === 'true' || v === '1' || v === 'yes';
}

/**
 * May this site SEND an SMS? A provisioned number AND outbound SMS not switched off.
 * Every send button, picker and channel default gates on this.
 *
 * NOTE: the backend can additionally return `twilio-misconfigured` (a number exists but
 * the subaccount creds are gone) because it can read teamio.sites. The frontend cannot
 * see that table, so a provisioned number reads as available here — deliberately the
 * same "omit when unknown" contract resolveChannels documents, so the two agree on the
 * common case and only the backend surfaces the broken-office state.
 *
 * @param {object|Array|null|undefined} settings
 * @returns {boolean}
 */
export function isSmsEnabled(settings) {
  return hasSmsNumber(settings) && !isSmsOutboundDisabled(settings);
}

/**
 * Why SMS is or isn't available — the backend resolver's vocabulary, so the two copies can
 * be compared value for value. No number outranks the policy: an office without Teamio
 * texting is a Core-plan office, not a WhatsApp-only one (different staff copy).
 *
 * @param {object|Array|null|undefined} settings
 * @returns {'ok'|'no-teamio-texting'|'sms-disabled'}
 */
export function smsReason(settings) {
  if (!hasSmsNumber(settings)) return 'no-teamio-texting';
  if (isSmsOutboundDisabled(settings)) return 'sms-disabled';
  return 'ok';
}

/**
 * Has Teamio switched WhatsApp on for this site? The server writes the string 'true'
 * (saveWhatsAppSettings / the onboarding go-live); nothing else counts.
 *
 * @param {object|Array|null|undefined} settings
 * @returns {boolean}
 */
export function isWhatsAppEnabled(settings) {
  const raw = readSettingValue(settings, WHATSAPP_ENABLED_SETTING);
  return typeof raw === 'string' && raw.trim().toLowerCase() === 'true';
}

/**
 * Should this site see Conversations (nav, inbox listener, unread badge)? A texting
 * number OR WhatsApp. NOT isSmsEnabled: switching outbound SMS off must not hide the inbox
 * where inbound SMS and every WhatsApp thread live.
 *
 * @param {object|Array|null|undefined} settings
 * @returns {boolean}
 */
export function canUseConversations(settings) {
  return hasSmsNumber(settings) || isWhatsAppEnabled(settings);
}

// ── Online Scheduling (2026-10-19) ───────────────────────────────────────────
// ONLINE_SCHEDULING_PAID_FOR is the per-site entitlement the Online Appointments page, the nav (config/ability.js)
// and teamio-utility's send time (reminderMessageDefaults resolveBookingLinkPlan: trimmed, any case, 'TRUE') all read.
export const ONLINE_SCHEDULING_SETTING = 'ONLINE_SCHEDULING_PAID_FOR';

/**
 * Does this site have Online Scheduling? True only for 'TRUE' (trimmed, any case) — the SAME reading the layer's
 * resolveBookingLinkPlan applies at send time, so the settings screen never offers an online-scheduling link the
 * sender would swap for the office phone. Absent / blank / anything else: no.
 *
 * @param {object|Array|null|undefined} settings
 * @returns {boolean}
 */
export function isOnlineSchedulingPaid(settings) {
  const raw = readSettingValue(settings, ONLINE_SCHEDULING_SETTING);
  return typeof raw === 'string' && raw.trim().toUpperCase() === 'TRUE';
}

/**
 * Can this site send email? Delegates to the real five-shape parser rather than testing
 * the string for emptiness — "has a verified sender" is the entitlement question, and
 * verifiedSender.js is explicit that pickVerifiedFromEmail (not resolveFromEmail, whose
 * fallback would mask it) is the way to ask.
 *
 * @param {object|Array|null|undefined} settings
 * @returns {boolean}
 */
export function isEmailEnabled(settings) {
  return pickVerifiedFromEmail(readSettingValue(settings, EMAIL_CAPABILITY_SETTING)) !== null;
}

/**
 * Both capabilities at once, shaped like the backend resolver's summary. Handy for
 * components that gate several controls off the same answer and would otherwise call
 * the two helpers repeatedly inside a template.
 *
 * @param {object|Array|null|undefined} settings
 * @returns {{ sms: boolean, smsReason: 'ok'|'no-teamio-texting'|'sms-disabled',
 *            email: boolean, fromEmail: string|null, anyAvailable: boolean }}
 */
export function resolveSiteChannels(settings) {
  const sms = isSmsEnabled(settings);
  const fromEmail = pickVerifiedFromEmail(readSettingValue(settings, EMAIL_CAPABILITY_SETTING));
  return { sms, smsReason: smsReason(settings), email: fromEmail !== null, fromEmail, anyAvailable: sms || fromEmail !== null };
}
